import { useState, useEffect, type FormEvent } from 'react';
import { format } from 'date-fns';
import { useAdminSession } from '@/hooks/useAdminSession';
import { useAiSettings } from '@/hooks/useAiSettings';
import { adminApi } from '@/lib/adminApi';
import { buildSlug } from '@/lib/postContent';
import { getProvider } from '@publishweaver/ai-providers';
import { MarkdownEditor } from '@publishweaver/markdown-editor';
import '@publishweaver/markdown-editor/css';
import { MarkdownContent } from '@/components/blog/MarkdownContent';

interface PrefillData {
  title?: string;
  slug?: string;
  description?: string;
  category?: string;
  tags?: string;
  content?: string;
}

interface Props {
  slug?: string;
  prefill?: Partial<PrefillData>;
  onSuccess: (slug: string) => void;
  onCancel: () => void;
}

const TODAY = new Date().toISOString().split('T')[0]!;

export const PostEditorPage = ({ slug: editSlug, prefill, onSuccess, onCancel }: Props) => {
  const { token } = useAdminSession();
  const { settings, toProviderConfig } = useAiSettings();
  const isEditMode = editSlug !== undefined;

  const [title, setTitle] = useState(prefill?.title ?? '');
  const [slug, setSlug] = useState(prefill?.slug ?? '');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(!!prefill?.slug);
  const [description, setDescription] = useState(prefill?.description ?? '');
  const [date, setDate] = useState(TODAY);
  const [author, setAuthor] = useState('Chad Crossman');
  const [category, setCategory] = useState(prefill?.category ?? '');
  const [tags, setTags] = useState(prefill?.tags ?? '');
  const [featured, setFeatured] = useState(false);
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [content, setContent] = useState(prefill?.content ?? '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(isEditMode);
  const [generatingMeta, setGeneratingMeta] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (!isEditMode || !token || !editSlug) return;
    setLoadingPost(true);
    adminApi.getPost(token, editSlug)
      .then((post) => {
        setTitle(post.title);
        setSlug(post.slug);
        setSlugManuallyEdited(true);
        setDescription(post.description);
        setDate(post.date);
        setAuthor(post.author);
        setCategory(post.category);
        setTags(post.tags.join(', '));
        setFeatured(post.featured);
        setStatus(post.status === 'deleted' ? 'draft' : post.status);
        setContent(post.content);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load post');
      })
      .finally(() => { setLoadingPost(false); });
  }, [isEditMode, token, editSlug]);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugManuallyEdited) {
      setSlug(buildSlug(value));
    }
  };

  const handleGenerateMeta = async () => {
    if (!content.trim()) return;
    setGeneratingMeta(true);
    setError(null);
    try {
      const provider = getProvider(settings.providerId);
      const draft = await provider.generateBlogPost({ content: content.trim(), config: toProviderConfig() });
      if (!title)       { setTitle(draft.title); if (!slugManuallyEdited) setSlug(buildSlug(draft.slug)); }
      if (!description) setDescription(draft.description);
      if (!category)    setCategory(draft.category);
      if (!tags)        setTags(draft.tags.join(', '));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Metadata generation failed');
    } finally {
      setGeneratingMeta(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setLoading(true);
    try {
      const tagList = tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      if (isEditMode && editSlug) {
        const { slug: saved } = await adminApi.updatePost(token, editSlug, {
          title,
          description,
          date,
          author,
          category,
          tags: tagList,
          featured,
          status,
          content,
        });
        onSuccess(saved);
      } else {
        const { slug: created } = await adminApi.createPost(token, {
          slug,
          title,
          description,
          date,
          author,
          category,
          tags: tagList,
          featured,
          status,
          content,
        });
        onSuccess(created);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  const submitLabel = () => {
    if (loading) return 'Saving…';
    if (isEditMode) return 'Save changes';
    return status === 'draft' ? 'Save draft' : 'Publish post';
  };

  if (loadingPost) {
    return (
      <div className="admin-shell">
        <header className="admin-topbar">
          <span className="admin-topbar__brand">Studio — Edit post</span>
          <button className="admin-btn admin-btn--ghost" onClick={onCancel}>Cancel</button>
        </header>
        <main className="admin-main">
          <p className="admin-status">Loading post…</p>
        </main>
      </div>
    );
  }

  if (previewMode) {
    const formattedDate = date ? format(new Date(date), 'dd MMMM yyyy') : '';
    return (
      <div className="admin-shell">
        <header className="admin-topbar">
          <span className="admin-topbar__brand">Studio — Preview</span>
          <div className="admin-topbar__actions">
            <button
              className="admin-btn admin-btn--ghost admin-btn--sm"
              onClick={() => { setPreviewMode(false); }}
            >
              ← Back to editing
            </button>
          </div>
        </header>
        <main className="admin-main admin-main--preview">
          <article className="article-shell">
            <header className="article-header">
              {category && <p className="article-header__eyebrow">{category}</p>}
              <h1>{title || 'Untitled'}</h1>
              {description && <p className="article-header__dek">{description}</p>}
              <div className="article-header__meta">
                {formattedDate && <span>{formattedDate}</span>}
                {author && <span>{author}</span>}
              </div>
            </header>
            <div className="article-content">
              <MarkdownContent content={content} />
            </div>
          </article>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <span className="admin-topbar__brand">
          {isEditMode ? 'Studio — Edit post' : 'Studio — New post'}
        </span>
        <div className="admin-topbar__actions">
          {content.trim() && (
            <button
              type="button"
              className="admin-btn admin-btn--ghost admin-btn--sm"
              disabled={generatingMeta}
              onClick={() => { void handleGenerateMeta(); }}
            >
              {generatingMeta ? 'Generating…' : 'Generate metadata'}
            </button>
          )}
          <button
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={() => { setPreviewMode(true); }}
          >
            Preview
          </button>
          <button className="admin-btn admin-btn--ghost admin-btn--sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </header>

      <main className="admin-main">
        <form className="admin-form admin-form--wide" onSubmit={handleSubmit} autoComplete="off">
          <div className="admin-form-actions">
            <button type="submit" className="admin-btn" disabled={loading}>
              {submitLabel()}
            </button>
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onCancel}>
              Cancel
            </button>
          </div>

          <div className="admin-fields-row">
            <div className="admin-field">
              <label htmlFor="title" className="admin-label">Title *</label>
              <input
                id="title"
                type="text"
                className="admin-input"
                value={title}
                onChange={(e) => { handleTitleChange(e.target.value); }}
                required
              />
            </div>
            <div className="admin-field">
              <label htmlFor="slug" className="admin-label">Slug *</label>
              <input
                id="slug"
                type="text"
                className="admin-input admin-input--mono"
                value={slug}
                onChange={(e) => { setSlug(e.target.value); setSlugManuallyEdited(true); }}
                required
                readOnly={isEditMode}
              />
            </div>
          </div>

          <div className="admin-field">
            <label htmlFor="desc" className="admin-label">Description *</label>
            <input
              id="desc"
              type="text"
              className="admin-input"
              value={description}
              onChange={(e) => { setDescription(e.target.value); }}
              required
            />
          </div>

          <div className="admin-fields-row">
            <div className="admin-field">
              <label htmlFor="date" className="admin-label">Date</label>
              <input
                id="date"
                type="date"
                className="admin-input"
                value={date}
                onChange={(e) => { setDate(e.target.value); }}
              />
            </div>
            <div className="admin-field">
              <label htmlFor="author" className="admin-label">Author</label>
              <select
                id="author"
                className="admin-input"
                value={author}
                onChange={(e) => { setAuthor(e.target.value); }}
              >
                <option value="Chad Crossman">Chad Crossman</option>
                <option value="PublishWeaver">PublishWeaver</option>
              </select>
            </div>
            <div className="admin-field">
              <label htmlFor="category" className="admin-label">Category *</label>
              <input
                id="category"
                type="text"
                className="admin-input"
                value={category}
                onChange={(e) => { setCategory(e.target.value); }}
                required
              />
            </div>
          </div>

          <div className="admin-fields-row">
            <div className="admin-field">
              <label htmlFor="tags" className="admin-label">Tags (comma-separated)</label>
              <input
                id="tags"
                type="text"
                className="admin-input"
                value={tags}
                onChange={(e) => { setTags(e.target.value); }}
                placeholder="product, engineering, ai"
              />
            </div>
            <div className="admin-field">
              <label htmlFor="status" className="admin-label">Status</label>
              <select
                id="status"
                className="admin-input"
                value={status}
                onChange={(e) => { setStatus(e.target.value as 'draft' | 'published'); }}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            <div className="admin-field admin-field--checkbox">
              <label className="admin-label admin-label--checkbox">
                <input
                  type="checkbox"
                  checked={featured}
                  onChange={(e) => { setFeatured(e.target.checked); }}
                />
                Featured post
              </label>
            </div>
          </div>

          <div className="admin-field admin-field--grow">
            <label className="admin-label">Content</label>
            <MarkdownEditor
              value={content}
              onChange={(v) => { setContent(v); }}
              minHeight="500px"
              showCodeBlock
            />

          </div>

          {error && <p className="admin-error">{error}</p>}

          <div className="admin-form-actions">
            <button type="submit" className="admin-btn" disabled={loading}>
              {submitLabel()}
            </button>
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};
